import { useCallback, useRef, useState } from 'react';

import { BackLink } from '@/components/BackLink';
import { Button } from '@/components/Button';
import { Callout } from '@/components/Callout';
import { KioskCard } from '@/components/KioskCard';
import { KioskScreen } from '@/components/KioskScreen';
import { StatusBadge } from '@/components/StatusBadge';
import { useTranslation } from '@/i18n/useTranslation';
import { SCREEN, useKioskSession } from '@/lib/kioskSession';
import { noticeI9 } from '@/services/i9';
import { useBack } from '@/shared/hooks/useBack';
import { useFocusOnMount } from '@/shared/hooks/useFocusOnMount';

import { DocumentsQrDialog } from './DocumentsQrDialog';
import { DocumentsSentence } from './DocumentsSentence';

/**
 * The I-9 information step [KAP-6; Figma `12 · I-9 info — Desktop` / `— iPad` / `— Mobile`, resolved by
 * name on the kiosk page 2026-09-11].
 *
 * **The kiosk collects nothing here — not even Section 1** [decision 0050]. It explains what the I-9 is,
 * that Section 1 lives in the candidate's Talent Portal, and that Section 2 is completed in person by an
 * Authorized Representative [decision 0004]. Then it asks one question and gets out of the way.
 *
 * ## The question, and why the two buttons are back
 *
 * ⭐ **The queue entry belongs to the BUTTON, never to the arrival-menu selection** [the Operator,
 * 2026-09-11; decision 0206, amending 0069]: *"La llamada para i9 a la queue solo debe darse si toco en el
 * boton yes, help me today. Por eso hacen falta los dos botones."* Choosing *Complete my I-9* on
 * `What brings you in today?` raises nothing at all now, and this screen is where a candidate can ask.
 *
 * - **Yes, help me today** — sends `assistance-requested`, which is what raises the *I-9 in person* entry
 *   on the recruiter's queue — or adds that reason to the candidate's open entry when another path already
 *   queued them [KOP-1d AC-1, merged by the api]. Decision 0060's *exactly one notification* is unchanged;
 *   it is now the reason the emit sits on the button rather than in an effect, where it used to fire for
 *   everyone who merely reached the screen.
 * - **No, someone else will** — returns to `What brings you in today?` and raises nothing [the Operator,
 *   2026-09-11]. It coincides with Back today because the arrival menu is this screen's only entrance; it
 *   is written out rather than delegated to `useBack` because it is a ruling about this control, not the
 *   generic back rule.
 *
 * ⚠️ **Nothing here gates.** No wait, no spinner tied to the notice, no "waiting for a representative"
 * state, and no dependency on an acknowledgement [decisions 0060, 0046]. KAP-6 states that twice, because
 * the notice is exactly the mechanism a wait grows around.
 *
 * ⚠️ **The copy is ISS's, taken from the frame verbatim** [decision 0040] — the question and both labels
 * included. Part 2's two remote-representative sentences stay removed: they offered the friend-or-family
 * Authorized Representative that [0050] retired, and an approved decision is not overridden by a frame that
 * has not caught up (convention skill § 8). **That contradiction is OQ-94 + OQ-111 and is not settled by
 * this screen** — the buttons are here because the Operator asked for them, not because 0050 moved.
 *
 * ⚠️ The frame's callout component still carries an `Action link →` placeholder with no destination in the
 * file. It is NOT built: a link to nowhere on an unattended kiosk is worse than no link. Owed by the
 * designer.
 *
 * ## The documents sentence, and the one QR this kiosk may show
 *
 * ISS's sentence sits between the two parts and the callout, as the frame draws it [decisions 0300, 0341].
 * Its *please click here* opens `DocumentsQrDialog` — a QR code for the candidate's OWN phone, and nothing
 * opened on the kiosk [0370]. ⛔ **It changes nothing else on the step** [KAP-6]: it sends no notice, moves
 * no screen, and the two answers below behave exactly as they do without it. Closing the overlay leaves the
 * candidate where they were, with focus back on the control that opened it.
 */
export function I9Screen() {
  const t = useTranslation();
  const back = useBack();
  const heading = useFocusOnMount<HTMLHeadingElement>();
  const goTo = useKioskSession((state) => state.goTo);
  const candidateId = useKioskSession((state) => state.lookupResult?.candidateId);
  const noticeSent = useRef(false);
  const noteRepresentativeSummoned = useKioskSession((state) => state.noteRepresentativeSummoned);
  const [documentsQrOpen, setDocumentsQrOpen] = useState(false);
  const documentsControl = useRef<HTMLButtonElement | null>(null);

  const openDocumentsQr = useCallback(() => {
    setDocumentsQrOpen(true);
  }, []);

  // Stable, because the overlay's key listener is bound to it.
  const closeDocumentsQr = useCallback(() => {
    setDocumentsQrOpen(false);
    // Back to the control that opened it — the step was never left, so the place is still there [0370].
    documentsControl.current?.focus();
  }, []);

  function askForHelp(): void {
    /*
     * ⭐ **Sent even when this candidate is already queued** [KOP-1d AC-1; 0383]. A candidate summoned earlier
     * in the visit (*Need help?*, the drug-test screen) has one open entry, and *I-9 in person* must join it
     * — one entry carries both reasons. The api merges a second cause into the open entry rather than raising
     * a second row, so the cross-path half of 0060's *exactly one* is the server's now. Guarding on
     * `recruiterSummoned` here is what left that entry without its I-9 reason.
     *
     * A ref, not the session flag: the only duplicate this screen still owns is its OWN repeat tap. A later
     * visit to the step sends again, and the merge's *a reason already there changes nothing* absorbs it.
     */
    if (!noticeSent.current) {
      noticeSent.current = true;
      void noticeI9('assistance-requested', candidateId);
    }
    /*
     * Recorded unconditionally, so the screens downstream that promise a representative do not raise a
     * SECOND, differently labelled row for the same person: the label is the only thing telling a recruiter
     * what they need, and this candidate's carries the I-9 one.
     */
    noteRepresentativeSummoned();
    // Immediately. The notice is in flight and nothing waits on it [0060, 0046].
    goTo(SCREEN.finish);
  }

  function declineHelp(): void {
    // Raises nothing, by the ruling. A candidate whose I-9 is somebody else's errand has asked for no one.
    goTo(SCREEN.arrivalReason);
  }

  return (
    <KioskScreen>
      <KioskCard
        footer={
          <>
            {/*
              The frame's `Assist question`, read live 2026-09-11: 14/24 semibold, `--muted-foreground`
              (#585756 — the exact value the frame carries), left-aligned across the full footer width.
            */}
            <p className="w-full text-sm font-semibold leading-6 text-[var(--muted-foreground)]">
              {t('i9.assistQuestion')}
            </p>
            {/*
              `Actions`: HORIZONTAL, 12px gap, both children `layoutGrow: 1` — so an even split, which is
              `flex-1` under `w-full`.

              ⚠️ **Stacked below `sm`, and that is a departure the frame forces.** At 375 the frame draws
              this row 730 wide inside a 375 card, and at 820 it places `Yes` at x=-101, clipped in the file
              itself. Reproducing either faithfully means shipping the clipping. Stacking is the resolution
              recorded in `design-qa/config.json` against the frame update that owes it.
            */}
            <div className="flex w-full flex-col gap-3 sm:flex-row">
              <Button fullWidth onClick={askForHelp}>
                {t('i9.helpYes')}
              </Button>
              {/*
                `outline`, not `secondary`: the frame gives this one a 1.5px `--primary` stroke and a
                `--primary` label, where every earlier frame draws the declining slot in body text. The
                Button component carries both treatments for exactly this reason.
              */}
              <Button fullWidth onClick={declineHelp} variant="outline">
                {t('i9.helpNo')}
              </Button>
            </div>
          </>
        }
        header={
          <>
            <BackLink onClick={back} />
            <StatusBadge label={t('badge.kiosk')} />
          </>
        }
      >
        <div className="flex w-full flex-col items-start gap-3 pb-10">
          <span
            aria-hidden="true"
            className="flex h-14 w-14 items-center justify-center self-center rounded-full bg-[var(--primary-muted)]"
          >
            <svg fill="none" height="24" viewBox="0 0 24 24" width="24">
              <path
                d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6Z"
                stroke="var(--primary)"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
              />
              <path
                d="M14 2v6h6M9 13h6M9 17h6"
                stroke="var(--primary)"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
              />
            </svg>
          </span>
          <h1
            className="kiosk-heading-focus w-full text-center text-2xl font-bold leading-7 text-black"
            ref={heading}
            tabIndex={-1}
          >
            {t('i9.heading')}
          </h1>
          <p className="w-full text-center text-sm leading-6 text-[var(--muted-foreground)]">
            {t('i9.body')}
          </p>
          {/*
            An ordered list, not two paragraphs: the two parts happen in an order, one of them is not the
            candidate's to do, and the numerals are the only thing that says so at a glance. `<ol>` gives a
            screen reader the same count the teal numerals give everybody else — "list, 2 items".

            The markers are drawn rather than left to `list-style`, because the frame colours them
            `--primary` while the text stays `--foreground` and a CSS marker cannot be styled apart from
            its item in every browser this kiosk may run.
          */}
          {/*
            `role="list"` is NOT redundant here. Safari removes list semantics from a list styled
            `list-style: none` — and the markers are drawn by hand precisely because the frame colours them
            differently from the text — so on the iPad this build targets, the "list, 2 items" a screen
            reader should announce would silently not be. jsdom computes the role from the tag and can
            never catch it.
          */}
          {/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- NOT redundant on WebKit: Safari drops list semantics from a list styled `list-style: none`, and this list is `list-none` by design. */}
          <ol className="flex w-full list-none flex-col gap-3" role="list">
            {[t('i9.section1'), t('i9.section2')].map((part, index) => (
              <li className="flex w-full items-start gap-2" key={part}>
                <span aria-hidden="true" className="text-sm leading-5 text-[var(--primary)]">
                  {index + 1}
                </span>
                <span className="flex-1 text-sm leading-5 text-[var(--foreground)]">{part}</span>
              </li>
            ))}
          </ol>
          <DocumentsSentence controlRef={documentsControl} onOpen={openDocumentsQr} />
          <Callout>{t('i9.staffCanHelp')}</Callout>
        </div>
      </KioskCard>
      {documentsQrOpen ? <DocumentsQrDialog onClose={closeDocumentsQr} /> : null}
    </KioskScreen>
  );
}
